import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import api from '../../services/api';
import '../../styles/AgendaSettimana.css';
import { oggiClinica } from '../../utils/oraItaliana';

/**
 * AgendaSettimana — una sola agenda su sette giorni.
 *
 * Non tutte le agende su tutta la settimana: con dodici medici in sede
 * sarebbe illeggibile. Il medico apre la propria settimana, la
 * segreteria sceglie di chi guardarla dalla tendina.
 *
 * Le colonne qui sono i giorni, non le agende, ma il posizionamento e'
 * lo stesso della vista giornaliera: ogni ora vale ALTEZZA_ORA pixel e
 * tutto si colloca in assoluto rispetto a ORA_INIZIO.
 */

const ORA_INIZIO = 7;
const ORA_FINE = 22;
const ALTEZZA_ORA = 60;

const GIORNI = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

const dueCifre = (n) => String(n).padStart(2, '0');

/** "08:45:00" → 8.75 */
const oraDiTesto = (t) => {
    if (!t) return ORA_INIZIO;
    const [h, m] = String(t).split(':');
    return parseInt(h, 10) + parseInt(m, 10) / 60;
};

/** ISO → 14.5 */
const oraDiData = (iso) => {
    const d = new Date(iso);
    return d.getHours() + d.getMinutes() / 60;
};

const orario = (iso) => {
    const d = new Date(iso);
    return `${dueCifre(d.getHours())}:${dueCifre(d.getMinutes())}`;
};

/** Il lunedì della settimana in cui cade la data indicata. */
/* I conti sui giorni si fanno a mezzogiorno UTC e con i metodi UTC:
   cosi' non dipendono dal fuso del browser e non inciampano nei giorni
   del cambio d'ora, che sono lunghi 23 o 25 ore. Con `T12:00:00` e i
   metodi locali funzionava, ma solo perche' la postazione e' in Italia.
   `oggiIso` invece era proprio sbagliato: prendeva il giorno UTC,
   quindi fra mezzanotte e le due la settimana mostrata era quella del
   giorno prima. */
const aMezzogiornoUtc = (iso) => new Date(iso + 'T12:00:00Z');

const lunediDi = (iso) => {
    const d = aMezzogiornoUtc(iso);
    const scarto = (d.getUTCDay() + 6) % 7;   // domenica in JS è 0
    d.setUTCDate(d.getUTCDate() - scarto);
    return d.toISOString().slice(0, 10);
};

const piuGiorni = (iso, n) => {
    const d = aMezzogiornoUtc(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

const oggiIso = () => oggiClinica();

/** Affianca gli appuntamenti che si sovrappongono nello stesso giorno. */
function disponi(lista) {
    const ordinati = [...lista].sort((a, b) => new Date(a.inizio) - new Date(b.inizio));
    const colonne = [];
    return ordinati.map((a) => {
        const inizio = new Date(a.inizio).getTime();
        const fine = new Date(a.fine).getTime();
        let col = colonne.findIndex((libero) => libero <= inizio);
        if (col === -1) { col = colonne.length; colonne.push(fine); }
        else colonne[col] = fine;
        const insieme = ordinati.filter((x) =>
            new Date(x.inizio).getTime() < fine && new Date(x.fine).getTime() > inizio);
        return { ...a, colonna: col, insieme: Math.max(insieme.length, col + 1) };
    });
}

const AgendaSettimana = ({ agende, agendaId, onCambiaAgenda, dataIniziale, onApri }) => {
    /* Il colore della colonna e' quello del medico dell'agenda scelta: la
       stanza quasi sempre non ne ha, e lo sfondo restava grigio. */
    const coloreAgenda = (agende || []).find((a) => a.id === agendaId)?.colore || '#cfd8dc';
    const [dal, setDal] = useState(lunediDi(dataIniziale || oggiIso()));
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(false);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        if (!agendaId) { setDati(null); return; }
        try {
            setCaricamento(true);
            setErrore(null);
            const r = await api.get('/agenda/settimana', {
                params: { dal, agenda_id: agendaId },
            });
            setDati(r.data.data);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile caricare la settimana');
        } finally {
            setCaricamento(false);
        }
    }, [dal, agendaId]);

    useEffect(() => { carica(); }, [carica]);

    const ore = useMemo(() => {
        const v = [];
        for (let h = ORA_INIZIO; h <= ORA_FINE; h += 1) v.push(h);
        return v;
    }, []);

    const giorni = useMemo(
        () => GIORNI.map((nome, i) => ({
            nome,
            n: i + 1,
            data: piuGiorni(dal, i),
        })),
        [dal]
    );

    const fascePer = (n) => (dati?.fasce || []).filter((f) => f.giorno === n);

    const appPer = (dataGiorno) => disponi(
        (dati?.appuntamenti || []).filter(
            (a) => String(a.inizio).slice(0, 10) === dataGiorno
        )
    );

    const etichettaAgenda = (a) =>
        (a.cognome ? `${a.cognome}, ${a.medico_nome}` : a.nome);

    return (
        <div className="sett-wrap">
            <div className="sett-testata">
                <div className="sett-navigazione">
                    <button onClick={() => setDal(lunediDi(oggiIso()))} className="sett-oggi">
                        Questa settimana
                    </button>
                    <button onClick={() => setDal(piuGiorni(dal, -7))} aria-label="Settimana precedente">
                        <ChevronLeft size={18} />
                    </button>
                    <button onClick={() => setDal(piuGiorni(dal, 7))} aria-label="Settimana successiva">
                        <ChevronRight size={18} />
                    </button>
                    <span className="sett-periodo">
                        {new Date(dal + 'T12:00:00').toLocaleDateString('it-IT',
                            { day: 'numeric', month: 'long' })}
                        {' – '}
                        {new Date(piuGiorni(dal, 6) + 'T12:00:00').toLocaleDateString('it-IT',
                            { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                </div>

                <select
                    className="sett-scelta"
                    value={agendaId || ''}
                    onChange={(e) => onCambiaAgenda(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                    <option value="">Scegli un&apos;agenda…</option>
                    {agende.map((a) => (
                        <option key={a.id} value={a.id}>{etichettaAgenda(a)}</option>
                    ))}
                </select>
            </div>

            {errore && <div className="ag-errore">{errore}</div>}

            {!agendaId ? (
                <div className="sett-stato">
                    <Calendar size={28} />
                    <p>Scegli un&apos;agenda per vederne la settimana.</p>
                </div>
            ) : caricamento && !dati ? (
                <div className="sett-stato">Caricamento…</div>
            ) : (
                <div className="sett-griglia">
                    <div className="sett-intestazioni">
                        <div className="sett-angolo" />
                        {giorni.map((g) => (
                            <div
                                key={g.n}
                                className={`sett-testa ${g.data === oggiIso() ? 'oggi' : ''}`}
                            >
                                <b>{g.nome}</b>
                                <span>
                                    {new Date(g.data + 'T12:00:00')
                                        .toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="sett-corpo">
                        <div
                            className="sett-interno"
                            style={{ height: (ORA_FINE - ORA_INIZIO + 1) * ALTEZZA_ORA }}
                        >
                            <div className="sett-ore">
                                {ore.map((h) => (
                                    <div key={h} className="sett-ora" style={{ height: ALTEZZA_ORA }}>
                                        <span>{dueCifre(h)}:00</span>
                                    </div>
                                ))}
                            </div>

                            {giorni.map((g) => (
                                <div key={g.n} className={`sett-colonna ${g.data === oggiIso() ? 'oggi' : ''}`}>
                                    {ore.map((h) => (
                                        <div
                                            key={h}
                                            className="sett-riga"
                                            style={{
                                                top: (h - ORA_INIZIO) * ALTEZZA_ORA,
                                                height: ALTEZZA_ORA,
                                            }}
                                        />
                                    ))}

                                    {fascePer(g.n).map((f, i) => {
                                        const da = oraDiTesto(f.ora_inizio);
                                        const al = oraDiTesto(f.ora_fine);
                                        return (
                                            <div
                                                key={i}
                                                className="sett-fascia"
                                                style={{
                                                    top: (da - ORA_INIZIO) * ALTEZZA_ORA,
                                                    height: (al - da) * ALTEZZA_ORA,
                                                    background: coloreAgenda + '22',
                                                }}
                                            />
                                        );
                                    })}

                                    {appPer(g.data).map((x) => {
                                        const da = oraDiData(x.inizio);
                                        const al = oraDiData(x.fine);
                                        const largh = 100 / x.insieme;
                                        return (
                                            <button
                                                key={x.id}
                                                className={`sett-app ag-stato-${x.stato}`}
                                                style={{
                                                    top: (da - ORA_INIZIO) * ALTEZZA_ORA,
                                                    height: Math.max((al - da) * ALTEZZA_ORA, 16),
                                                    left: `${x.colonna * largh}%`,
                                                    width: `calc(${largh}% - 2px)`,
                                                    borderLeftColor: coloreAgenda,
                                                }}
                                                onClick={() => onApri && onApri(x)}
                                            >
                                                <span className="sett-app-ora">{orario(x.inizio)}</span>
                                                <span className="sett-app-nome">
                                                    {x.paz_cognome
                                                        ? `${x.paz_cognome} ${x.paz_nome || ''}`
                                                        : (x.titolo || '—')}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgendaSettimana;
